/**
 * Onboarding/profile validation — the same rules the server applies to
 * PATCH /api/players/me (see backend/api-types.ts § UpdateMeRequest). The UI
 * uses it for inline form errors; both backends use it before saving.
 */
import type { ProfileInput } from '../contracts.ts';
import { PROFILE_LIMITS, type ProfileFieldProblem } from './backend/api-types.ts';
import { findSaProvince, HOME_COUNTRY } from './geo.ts';
import { containsBlockedWords, isNicknameAllowed, normalizeNickname } from './nicknames.ts';
import { collapseSpaces } from './text.ts';

export type ProfileValidation = { ok: true; value: ProfileInput } | { ok: false; problems: ProfileFieldProblem[] };

const PROFILE_FIELDS: readonly (keyof ProfileInput)[] = [
  'displayName',
  'grade',
  'schoolId',
  'schoolName',
  'city',
  'province',
  'country',
  'leaderboardOptIn',
  'locale',
];

/**
 * Characters allowed in school, city and province names: letters (any script,
 * with accents), digits, spaces and the punctuation real names use
 * ("St. Mary's DSG", "Hoërskool Dr. E.G. Jansen", "Pro Arte (Alphen Park)").
 * Rules out markup ("<", ">"), quotes, symbols and control characters.
 */
const PLACE_TEXT = /^[\p{L}\p{M}\p{N} '’.,&()/-]+$/u;

/** Why a school, city or province name can't be published, or null when it can. */
export type PlaceTextProblem = 'invalid-chars' | 'not-allowed';

/**
 * Moderation for public place text (after collapseSpaces): only letters,
 * digits, spaces and name punctuation (PLACE_TEXT), and nothing on the
 * nickname blocklist (containsBlockedWords). Length is checked separately.
 */
export function placeTextProblem(text: string): PlaceTextProblem | null {
  const t = collapseSpaces(text.normalize('NFC'));
  if (t.length > 0 && !PLACE_TEXT.test(t)) return 'invalid-chars';
  return containsBlockedWords(t) ? 'not-allowed' : null;
}

export function isProfileField(x: string): x is keyof ProfileInput {
  return PROFILE_FIELDS.some((f) => f === x);
}

/**
 * Validate and normalise a profile: whitespace collapsed, country upper-cased,
 * South African provinces stored as their code ("Wes-Kaap" → "WC").
 * Returns every problem at once so a form can mark all bad fields.
 */
export function validateProfileInput(input: ProfileInput): ProfileValidation {
  const problems: ProfileFieldProblem[] = [];
  const problem = (field: keyof ProfileInput, reason: string) => problems.push({ field, reason });
  /**
   * School, city and (free-text) province names are public: they appear in the
   * unauthenticated school/city type-ahead and on leaderboards. They get the
   * same moderation as nicknames: no markup or control characters, nothing on
   * the blocklist.
   */
  function checkPlaceText(field: 'schoolName' | 'city' | 'province', text: string): void {
    const reason = placeTextProblem(text);
    if (reason) problem(field, reason);
  }

  const displayName = normalizeNickname(input.displayName);
  const name = isNicknameAllowed(displayName);
  if (!name.ok) problem('displayName', name.reason ?? 'invalid');

  if (!Number.isInteger(input.grade) || input.grade < PROFILE_LIMITS.gradeMin || input.grade > PROFILE_LIMITS.gradeMax) {
    problem('grade', 'out-of-range');
  }

  const country = input.country.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(country)) problem('country', 'invalid');

  let province = collapseSpaces(input.province.normalize('NFC'));
  if (country === HOME_COUNTRY) {
    const p = findSaProvince(province);
    if (p) province = p.code;
    else problem('province', 'unknown-province');
  } else if (province.length > PROFILE_LIMITS.provinceMax) {
    problem('province', 'too-long');
  } else if (province.length > 0) {
    checkPlaceText('province', province);
  }

  const city = collapseSpaces(input.city.normalize('NFC'));
  if (city.length === 0) problem('city', 'required');
  else if (city.length > PROFILE_LIMITS.cityMax) problem('city', 'too-long');
  else checkPlaceText('city', city);

  const schoolName = collapseSpaces(input.schoolName.normalize('NFC'));
  if (schoolName.length < PROFILE_LIMITS.schoolNameMin) problem('schoolName', 'too-short');
  else if (schoolName.length > PROFILE_LIMITS.schoolNameMax) problem('schoolName', 'too-long');
  else checkPlaceText('schoolName', schoolName);

  if (input.locale !== 'en' && input.locale !== 'af') problem('locale', 'invalid');
  if (typeof input.leaderboardOptIn !== 'boolean') problem('leaderboardOptIn', 'invalid');

  if (problems.length > 0) return { ok: false, problems };
  const schoolId = input.schoolId?.trim() || null;
  return {
    ok: true,
    value: {
      displayName,
      grade: input.grade,
      schoolId,
      schoolName,
      city,
      province,
      country,
      leaderboardOptIn: input.leaderboardOptIn,
      locale: input.locale,
    },
  };
}
